import React, { useState, useRef, useEffect } from 'react';
import { MapPin } from 'lucide-react';
import { Input } from './ui/input';

// Autocomplétion d'adresse via la Base Adresse Nationale (api-adresse.data.gouv.fr) — gratuit, sans clé, adresses en France uniquement.
export const AddressAutocomplete = ({ value, onChange, placeholder, testId, required }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const formatLabel = (f) => {
    const p = f.properties || {};
    if (p.label) return p.label;
    const parts = [];
    if (p.name) parts.push(p.name);
    const line2 = [p.postcode, p.city].filter(Boolean).join(' ');
    if (line2) parts.push(line2);
    return parts.join(', ');
  };

  const fetchSuggestions = (q) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=5`
        );
        const data = await res.json();
        setSuggestions(data.features || []);
        setOpen(true);
      } catch (e) {
        setSuggestions([]);
      }
    }, 300);
  };

  const handleInput = (e) => {
    onChange(e.target.value);
    fetchSuggestions(e.target.value);
  };

  const handleSelect = (f) => {
    onChange(formatLabel(f));
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="relative" ref={boxRef}>
      <Input
        value={value}
        onChange={handleInput}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        required={required}
        autoComplete="off"
        placeholder={placeholder}
        className="bg-slate-950 border-slate-700 text-white focus:border-amber-500"
        data-testid={testId}
      />
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 w-full mt-1 bg-slate-900 border border-slate-700 rounded-md max-h-60 overflow-auto shadow-xl"
          data-testid={`${testId}-suggestions`}
        >
          {suggestions.map((f, i) => (
            <li
              key={(f.properties && f.properties.id) || formatLabel(f) || i}
              onClick={() => handleSelect(f)}
              className="px-3 py-2 text-sm text-gray-200 hover:bg-amber-600/20 cursor-pointer flex items-start gap-2 border-b border-slate-800 last:border-0"
              data-testid={`${testId}-suggestion-${i}`}
            >
              <MapPin className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span>{formatLabel(f)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
